import { useState } from 'react'
import { Button, ConfirmDialog, FieldBlock, Input, LoadingState, Textarea } from '@orbind/ui'
import {
  OPCOES_DE_SILENCIO,
  TOGGLE_CONTATOS_EXTERNOS,
  estaSilenciada,
  podeModerarCanal,
  textoDoSilencio,
  type CanalDeChat,
  type DuracaoDoSilencio,
  type EstadoPresenca,
  type MembroDoCanal,
} from '@orbind/core'
import { useFeatureToggle } from '../../lib/feature-toggle.ts'
import { usePresence } from '../presence/PresenceProvider.tsx'
import { AcervoDeMidia, Miniatura } from './AcervoDeMidia.tsx'
import { AdicionarPessoas, type Candidato } from './AdicionarPessoas.tsx'
import { FotoDoGrupo } from './FotoDoGrupo.tsx'
import { avatarDoAutor, urlDaFoto, urlDoBanner, useAcervo, useContatos, useFavoritas, useFixadas } from './useChat.ts'

/**
 * Dados do grupo — abre ao acionar o nome no topo da conversa.
 *
 * Montado com as peças da gaveta do protótipo (`ch-drawer__head/body`,
 * `ch-contact`, `ch-avatar`, `ch-dot`) — só a coluna à direita é nova, porque o
 * protótipo não previa este painel.
 *
 * Quem não modera vê as ações de administrar APAGADAS, não escondidas.
 */

/** Presença do Orbind -> variantes de `ch-dot` que o protótipo já desenha. */
const DOT: Record<EstadoPresenca, string> = {
  disponivel: 'online',
  /* no computador também é estar por perto: mesmo desenho do online aqui, onde
     não há espaço para o tom mais fraco que o painel de membros usa */
  'no-computador': 'online',
  ausente: 'ausente',
  offline: 'offline',
  desconectado: 'offline',
}

export function GroupInfo({
  canal,
  membros,
  onFechar,
  onAlterar,
  onAdicionar,
  onRemover,
  onIrParaMensagem,
  onTrocarFoto,
  onRemoverFoto,
  onSilenciar,
}: {
  canal: CanalDeChat
  membros: readonly MembroDoCanal[]
  onFechar: () => void
  onAlterar: (dados: { name?: string; description?: string | null }) => void
  onAdicionar: (memberIds: string[]) => void
  onRemover: (memberId: string) => void
  onIrParaMensagem: (channelId: string, messageId: string) => void
  onTrocarFoto: (arquivo: File) => void
  onRemoverFoto: () => void
  onSilenciar: (duracao: DuracaoDoSilencio | null) => void
}) {
  const silenciada = estaSilenciada(canal.silenciadaAte)
  const { membros: todosDoWorkspace, presencas, eu } = usePresence()
  const acervo = useAcervo(canal.id)
  const [acervoAberto, setAcervoAberto] = useState(false)
  const totalDoAcervo =
    (acervo.data?.midia.length ?? 0) + (acervo.data?.documentos.length ?? 0) + (acervo.data?.links.length ?? 0)
  const fixadas = useFixadas(canal.id)
  const favoritas = useFavoritas()

  const [editando, setEditando] = useState(false)
  const [nome, setNome] = useState(canal.name)
  const [descricao, setDescricao] = useState(canal.description ?? '')
  const [adicionando, setAdicionando] = useState(false)
  const [paraRemover, setParaRemover] = useState<MembroDoCanal | null>(null)

  const modero = podeModerarCanal(canal.meuPapel)
  const jaEstao = new Set(membros.map((m) => m.memberId))
  // Da empresa entra direto; contato aceito de outra empresa também (toggle
  // CHAT_EXTERNAL_CONTACTS) — o convite já foi aceito uma vez.
  const contatos = useContatos(useFeatureToggle(TOGGLE_CONTATOS_EXTERNOS))
  const candidatos: Candidato[] = [
    ...todosDoWorkspace.map((m) => ({
      id: m.id,
      name: m.name,
      sub: m.email,
      foto: avatarDoAutor({ memberId: m.id, avatarUrl: m.avatarUrl, hasAvatar: m.hasAvatar, avatarVersion: m.avatarVersion }),
    })),
    ...(contatos.data?.contatos ?? []).map((p) => ({
      id: p.memberId,
      name: p.name,
      sub: 'Contato de outra empresa',
      foto: null,
    })),
  ].filter((m, i, todos) => !jaEstao.has(m.id) && todos.findIndex((o) => o.id === m.id) === i)
  const direta = canal.kind === 'pessoa'
  const outraPessoa = direta ? (membros.find((m) => m.memberId !== eu) ?? null) : null
  const capaDaPessoa = outraPessoa ? urlDoBanner(outraPessoa) : null

  if (adicionando) {
    return (
      <aside className="ch-infocol" aria-label="Adicionar pessoas">
        <AdicionarPessoas
          candidatos={candidatos}
          onVoltar={() => setAdicionando(false)}
          onAdicionar={(ids) => {
            onAdicionar(ids)
            setAdicionando(false)
          }}
        />
      </aside>
    )
  }

  if (acervoAberto) {
    return (
      <aside className="ch-infocol" aria-label="Mídia, links e docs">
        <AcervoDeMidia
          channelId={canal.id}
          onVoltar={() => setAcervoAberto(false)}
          onIrParaMensagem={onIrParaMensagem}
        />
      </aside>
    )
  }

  return (
    <aside className="ch-infocol" aria-label={direta ? 'Dados da conversa' : 'Dados do grupo'}>
      <div className="ch-drawer__head">
        <button type="button" className="ch-drawer__back" aria-label="Fechar" onClick={onFechar}>
          <i className="fas fa-times" aria-hidden />
        </button>
        <span className="ch-drawer__id">
          <span className="ch-drawer__title">{direta ? 'Dados da conversa' : 'Dados do grupo'}</span>
          <span className="ch-drawer__sub">
            {membros.length} {membros.length === 1 ? 'membro' : 'membros'}
          </span>
        </span>
      </div>

      <div className="ch-drawer__body">
        <div className={`ch-info__card${editando ? '' : ' ch-info__identidade'}`}>
          {editando ? (
            <>
              <FieldBlock label="Nome do grupo" required>
                {({ id }) => <Input id={id} value={nome} onChange={(e) => setNome(e.target.value)} />}
              </FieldBlock>
              <FieldBlock label="Descrição">
                {({ id }) => (
                  <Textarea id={id} rows={3} value={descricao} onChange={(e) => setDescricao(e.target.value)} />
                )}
              </FieldBlock>
              <div className="ch-info__acoes">
                <Button variant="ghost" onClick={() => setEditando(false)}>
                  Cancelar
                </Button>
                <Button
                  disabled={nome.trim() === ''}
                  onClick={() => {
                    onAlterar({ name: nome.trim(), description: descricao.trim() || null })
                    setEditando(false)
                  }}
                >
                  Salvar
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Conversa direta: o banner do perfil da outra pessoa atrás da
                  foto dela, como no cartão de presença (2026-09-16). */}
              {capaDaPessoa ? (
                <span className="ch-info__capa" data-ob-ampliar>
                  <img src={capaDaPessoa} alt={`Banner de ${canal.name}`} />
                </span>
              ) : null}
              <span className="ch-avatarwrap ch-info__foto">
                <FotoDoGrupo
                  url={urlDaFoto(canal.foto)}
                  podeTrocar={modero && !direta}
                  onTrocar={onTrocarFoto}
                  onRemover={onRemoverFoto}
                />
              </span>
              <span className="ch-conv__title">{canal.name}</span>
              <span className="ch-conv__sub">
                {direta ? outraPessoa?.bio || 'Sem apresentação.' : canal.description || 'Sem descrição.'}
              </span>
              <Button
                variant="ghost"
                icon="fas fa-pen"
                disabled={!modero}
                title={modero ? undefined : 'Só quem modera altera o grupo'}
                onClick={() => setEditando(true)}
              >
                Editar
              </Button>
            </>
          )}
        </div>

        {/* Notificações: silenciar é escolha MINHA, vale só para mim. */}
        <section className="ch-info__card">
          <header className="ch-info__cabecalho">
            <i className={silenciada ? 'fas fa-bell-slash' : 'fas fa-bell'} aria-hidden />
            <span className="ch-info__rotulo">Notificações</span>
          </header>
          <span className="ch-contact__sub ch-info__silencio">{textoDoSilencio(canal.silenciadaAte)}</span>
          {silenciada ? (
            <Button variant="ghost" icon="fas fa-bell" onClick={() => onSilenciar(null)}>
              Reativar notificações
            </Button>
          ) : (
            <div className="ch-info__opcoes" role="group" aria-label="Silenciar por">
              <span className="ch-contact__sub">Silenciar por:</span>
              {OPCOES_DE_SILENCIO.map((opcao) => (
                <button key={opcao.id} type="button" className="ch-kindbtn" onClick={() => onSilenciar(opcao.id)}>
                  {opcao.rotulo}
                </button>
              ))}
            </div>
          )}
          <span className="ch-contact__sub ch-info__silencio">Menções a você continuam avisando.</span>
        </section>

        {/* Mídia, links e docs: o cabeçalho abre a aba maior; embaixo, as
            prévias das últimas mídias (referência: o mensageiro). */}
        <section className="ch-info__card">
          <button type="button" className="ch-info__cabecalho ch-info__abrir" onClick={() => setAcervoAberto(true)}>
            <i className="fas fa-photo-video" aria-hidden />
            <span className="ch-info__rotulo">Mídia, links e docs</span>
            <span className="ch-info__total">{totalDoAcervo}</span>
            <i className="fas fa-chevron-right ch-info__seta" aria-hidden />
          </button>
          {acervo.isPending ? (
            <LoadingState rows={1} label="Carregando mídias" />
          ) : (acervo.data?.midia.length ?? 0) > 0 ? (
            <div className="ch-info__previas">
              {acervo.data!.midia.slice(0, 4).map((item) => (
                <Miniatura key={item.id} item={item} />
              ))}
            </div>
          ) : totalDoAcervo > 0 ? (
            <button type="button" className="ch-contact__sub ch-info__resumo" onClick={() => setAcervoAberto(true)}>
              {acervo.data!.documentos.length} {acervo.data!.documentos.length === 1 ? 'documento' : 'documentos'} ·{' '}
              {acervo.data!.links.length} {acervo.data!.links.length === 1 ? 'link' : 'links'}
            </button>
          ) : (
            <span className="ch-contact__sub">Nada foi compartilhado ainda.</span>
          )}
        </section>

        <Secao icone="fas fa-thumbtack" titulo="Mensagens fixadas" total={fixadas.data?.length ?? 0}>
          {fixadas.isError ? (
            <span className="ch-contact__sub">Não foi possível carregar as fixadas. Reabra o painel.</span>
          ) : (fixadas.data ?? []).length === 0 ? (
            <span className="ch-contact__sub">Nenhuma mensagem fixada.</span>
          ) : (
            fixadas.data!.map((p) => (
              <LinhaDeMensagem
                key={p.messageId}
                autor={p.autor}
                texto={p.body}
                onClick={() => onIrParaMensagem(canal.id, p.messageId)}
              />
            ))
          )}
        </Secao>

        <Secao icone="fas fa-star" titulo="Mensagens favoritas" total={favoritas.data?.length ?? 0}>
          {favoritas.isError ? (
            <span className="ch-contact__sub">Não foi possível carregar as favoritas. Reabra o painel.</span>
          ) : (favoritas.data ?? []).length === 0 ? (
            <span className="ch-contact__sub">Você ainda não favoritou nenhuma mensagem.</span>
          ) : (
            favoritas.data!.map((f) => (
              <LinhaDeMensagem
                key={f.messageId}
                autor={`${f.autor ?? 'Alguém'} · ${f.channelName}`}
                texto={f.body}
                onClick={() => onIrParaMensagem(f.channelId, f.messageId)}
              />
            ))
          )}
        </Secao>

        <Secao icone="fas fa-users" titulo="Membros" total={membros.length}>
          <Button
            variant="ghost"
            icon="fas fa-user-plus"
            disabled={!modero || candidatos.length === 0}
            title={modero ? undefined : 'Só quem modera adiciona pessoas'}
            onClick={() => setAdicionando(true)}
          >
            Adicionar
          </Button>

          {membros.map((m) => {
            const estado = presencas.get(m.memberId)?.estado ?? 'desconectado'
            const foto = avatarDoAutor(m)
            return (
              <div key={m.memberId} className="ch-contact">
                <span className="ch-avatarwrap">
                  <span className="ch-contact__avatar">
                    {foto ? <img className="ch-avatar__img" src={foto} alt="" /> : <i className="fas fa-user" aria-hidden />}
                  </span>
                  <span className={`ch-dot ch-dot--${DOT[estado]}`} aria-hidden />
                </span>
                <span className="ch-contact__main">
                  <span className="ch-contact__name">{m.name}</span>
                  <span className="ch-contact__sub">{m.papel === 'moderator' ? 'Moderador' : m.email}</span>
                </span>
                <button
                  type="button"
                  className="ch-iconbtn"
                  aria-label={`Remover ${m.name}`}
                  disabled={!modero}
                  title={modero ? 'Remover do grupo' : 'Só quem modera remove participantes'}
                  onClick={() => setParaRemover(m)}
                >
                  <i className="fas fa-user-minus" aria-hidden />
                </button>
              </div>
            )
          })}
        </Secao>
      </div>

      <ConfirmDialog
        open={paraRemover !== null}
        title="Remover do grupo"
        description={`${paraRemover?.name ?? ''} deixa de ver esta conversa. As mensagens já enviadas continuam.`}
        confirmLabel="Remover"
        destructive
        onCancel={() => setParaRemover(null)}
        onConfirm={() => {
          if (paraRemover) onRemover(paraRemover.memberId)
          setParaRemover(null)
        }}
      />
    </aside>
  )
}

function Secao({
  icone,
  titulo,
  total,
  children,
}: {
  icone: string
  titulo: string
  total: number
  children: React.ReactNode
}) {
  return (
    <section className="ch-info__card">
      <header className="ch-info__cabecalho">
        <i className={icone} aria-hidden />
        <span className="ch-info__rotulo">{titulo}</span>
        <span className="ch-info__total">{total}</span>
      </header>
      {children}
    </section>
  )
}

function LinhaDeMensagem({ autor, texto, onClick }: { autor: string | null; texto: string; onClick: () => void }) {
  return (
    <button type="button" className="ch-contact" onClick={onClick}>
      <span className="ch-contact__main">
        <span className="ch-contact__name">{autor ?? 'Alguém'}</span>
        <span className="ch-contact__sub">{texto}</span>
      </span>
    </button>
  )
}
